import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, Loader2, ArrowRight, Download } from 'lucide-react';

interface UploadResult {
  success: boolean;
  message: string;
  totalRows?: number;
  successCount?: number;
  failedCount?: number;
  errors?: string[];
}

export default function UploadShipmentsPage() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/Shipments/excel-template', {
        method: 'GET',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('الملف فارغ');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = 'shipments_template.xlsx';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      console.error('Excel template download error:', error);
      alert('خطأ في تحميل نموذج Excel');
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type)) {
        alert('يرجى اختيار ملف Excel صحيح (.xlsx أو .xls)');
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('يرجى اختيار ملف أولاً');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/shipments/upload-excel', formData);

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message || 'تم رفع الشحنات بنجاح',
          totalRows: response.data.totalRows,
          successCount: response.data.successCount,
          failedCount: response.data.failedCount,
          errors: response.data.errors,
        });
      } else {
        setResult({
          success: false,
          message: response.data.message || 'خطأ في رفع الملف',
          errors: response.data.errors,
        });
      }
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || 'حدث خطأ أثناء الرفع',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50/30 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-8 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
          <button onClick={() => navigate('/shipments')} className="p-2 hover:bg-gray-100 rounded-full transition">
            <ArrowRight className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
              <FileSpreadsheet className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">رفع الشحنات جماعي</h1>
              <p className="text-gray-500 text-sm">رفع عدد كبير من الشحنات عبر ملف Excel</p>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4">كيفية الاستخدام</h2>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">1</div>
                <p>قم بتحميل نموذج Excel الجاهز بالضغط على زر "تحميل النموذج"</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">2</div>
                <p>املأ البيانات في الملف (اسم المرسل، المستلم، الهاتف، العنوان، المحافظة، المدينة)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">3</div>
                <p>اختر الملف واضغط "رفع الشحنات" لبدء العملية</p>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold transition shadow-lg"
              >
                <Download className="w-5 h-5" />
                تحميل النموذج
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-8 shadow-sm">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-10 h-10 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">سحب وإفلات الملف هنا</h3>
              <p className="text-gray-500 mb-4">أو اضغط لاختيار ملف Excel</p>
              
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-xl font-bold cursor-pointer transition shadow-lg"
              >
                <FileSpreadsheet className="w-5 h-5" />
                اختيار ملف
              </label>

              {selectedFile && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-6 h-6 text-green-600" />
                    <div className="text-right">
                      <p className="font-bold text-green-800">{selectedFile.name}</p>
                      <p className="text-sm text-green-600">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedFile && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-6 w-full bg-gradient-to-l from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold px-8 py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      جاري الرفع والمعالجة...
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6" />
                      رفع الشحنات
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {result && (
            <div className={`rounded-2xl border p-6 shadow-sm ${
              result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                {result.success ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
                <h3 className={`text-xl font-bold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                  {result.message}
                </h3>
              </div>

              {result.totalRows !== undefined && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <p className="text-sm text-gray-600">إجمالي الصفوف</p>
                    <p className="text-2xl font-bold text-gray-900">{result.totalRows}</p>
                  </div>
                  <div className="bg-green-100 p-4 rounded-xl shadow-sm">
                    <p className="text-sm text-green-700">تم بنجاح</p>
                    <p className="text-2xl font-bold text-green-800">{result.successCount || 0}</p>
                  </div>
                  <div className="bg-red-100 p-4 rounded-xl shadow-sm">
                    <p className="text-sm text-red-700">فشل</p>
                    <p className="text-2xl font-bold text-red-800">{result.failedCount || 0}</p>
                  </div>
                </div>
              )}

              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-bold text-red-800 mb-2">الأخطاء:</h4>
                  <ul className="space-y-1">
                    {result.errors.map((error, index) => (
                      <li key={index} className="text-red-700 text-sm">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.success && (
                <button
                  onClick={() => navigate('/shipments')}
                  className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3 rounded-xl transition"
                >
                  العودة إلى الشحنات
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
